import * as React from 'react';

import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import { UIButton } from '~shared/components/buttons';
import { DialogProps } from '~shared/components/Dialog';
import { Link } from '~shared/components/Link/Link';
import {
  PaywallDialog,
  PaywallUpgradeButton,
} from '~teacher/components/PaywallDialog';

type LibrarySettingsPaywallProps = Pick<DialogProps, 'open' | 'onClose'>;

/**
 * Modal shown when a non-Organization user clicks an ORG-gated library setting.
 * Mirrors the existing `SharedLibraryPaywall` shape (Figma 2806:1195) so we
 * inherit the analytics, copy frame, and button treatments used everywhere
 * else in the app.
 */
export function LibrarySettingsPaywall({
  open,
  onClose,
}: LibrarySettingsPaywallProps) {
  const paywallScreen = PaywallScreens.SharedLibrarySettingsOrg;

  return (
    <PaywallDialog.Long
      id="library-settings-paywall"
      open={open}
      onClose={onClose}
      title={translate('Control more with the Socrative Organization plan')}
      paywallTouch={[paywallScreen]}
      closeLabel={translate('Close')}
      actions={[
        <UIButton key="cancel" onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <PaywallUpgradeButton
          key="upgrade"
          appearance="primary"
          paywallScreen={paywallScreen}
        />,
      ]}
    >
      <div className="flex flex-col gap-6 text-blue-900">
        <div>
          <p className="m-0 font-500">
            {translate('Protect what your teachers create')}
          </p>
          <p className="m-0">
            {translate(
              'Set boundaries on how quizzes are copied, downloaded, and shared.'
            )}
          </p>
        </div>
        <div>
          <p className="m-0 font-500">{translate('Control who does what')}</p>
          <p className="m-0">
            {translate(
              'Decide which roles can edit, invite, and manage — so your space runs the way you need it to.'
            )}
          </p>
        </div>
        <div>
          <p className="m-0 font-500">
            {translate('Keep your community private')}
          </p>
          <p className="m-0">
            {translate(
              'Hide member lists from Viewers and Guests, and manage invitations at the admin level.'
            )}
          </p>
        </div>
        <Link href="/teacher/#pricing" target="_self">
          {translate('Compare Plans')}
        </Link>
      </div>
    </PaywallDialog.Long>
  );
}
